import React, { useEffect, useState } from 'react';
import { tcApi } from './tcApi';
import { fmtDueIst } from '../logic/followupDays';

/**
 * "⏰ Follow-up due <date time> · <agent> · <reason>" for a customer's OPEN call
 * follow-ups (tc/callbacks), red once the time has passed. Shown on the customer
 * profile and in the Customer 360, to whoever opens the customer — so a promise made
 * by an agent who is off today is still seen.
 *
 * Pass `followups` (from the 360 response) or just `mobile` to fetch
 * GET /api/tc/followups/customer (re-fetched whenever `refreshKey` changes).
 */
export function useCustomerFollowups(mobile, enabled = true, refreshKey = null) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        const m = String(mobile || '').replace(/\D/g, '').slice(-10);
        if (!enabled || m.length !== 10) { setRows(null); return undefined; }
        let stop = false;
        tcApi(`/api/tc/followups/customer?mobile=${m}`)
            .then(j => { if (!stop) setRows(j.followups || []); })
            .catch(() => { if (!stop) setRows([]); });   // never blocks the page
        return () => { stop = true; };
    }, [mobile, enabled, refreshKey]);
    return rows;
}

export default function FollowupDueBanner({ followups, mobile, refreshKey = null, className = '' }) {
    const fetched = useCustomerFollowups(mobile, !followups, refreshKey);
    const list = followups || fetched || [];
    if (!list.length) return null;
    const now = Date.now();
    return (
        <div className={`space-y-1 ${className}`}>
            {list.slice(0, 3).map(f => {
                const late = Number(f.dueAt) <= now;
                return (
                    <div key={f.id} className={`flex flex-wrap items-center gap-x-1.5 rounded-xl px-3 py-2 text-sm ring-1 ${late ? 'bg-red-50 ring-red-200 text-red-800' : 'bg-amber-50 ring-amber-200 text-amber-900'}`}>
                        <span aria-hidden>⏰</span>
                        <b>Follow-up {late ? 'overdue' : 'due'} {fmtDueIst(f.dueAt)}</b>
                        {f.ownerName && <span>· {f.ownerName}</span>}
                        {f.reason && <span className="opacity-80 truncate max-w-full">· {f.reason}</span>}
                    </div>
                );
            })}
            {list.length > 3 && <div className="text-[11px] text-slate-500 px-1">+{list.length - 3} more open follow-up{list.length - 3 === 1 ? '' : 's'}</div>}
        </div>
    );
}
